import React, { useEffect, useState } from 'react';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { formatDateTime } from '../../utils/dateTime';
import PaginationBar from '../../components/common/PaginationBar';

const cardStyle = {
  background: '#ffffff',
  borderRadius: '16px',
  padding: '24px',
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  border: '1px solid #e2e8f0',
  borderRadius: '10px',
  background: '#fff',
};

const formatMoney = (value) => {
  const num = Number(value || 0);
  return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const AdminPayouts = () => {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [txLoading, setTxLoading] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [payModal, setPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', note: '' });
  const [txPage, setTxPage] = useState(0);
  const [txMeta, setTxMeta] = useState({ totalPages: 1, totalElements: 0 });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [walletsRes, txRes] = await Promise.all([
        adminAPI.getMediatorRewardWallets(),
        adminAPI.getMediatorRewardTransactions({ page: txPage, size: 15 }),
      ]);
      setWallets(walletsRes.data?.data || []);
      const txData = txRes.data?.data || {};
      setTransactions(txData.data || []);
      setTxMeta({
        totalPages: txData.totalPages || 1,
        totalElements: txData.totalElements || 0,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load mediator rewards');
    } finally {
      setLoading(false);
      setTxLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [txPage]);

  const openPayModal = (wallet) => {
    setPayModal(wallet);
    setPayForm({ amount: wallet.balance > 0 ? String(wallet.balance) : '', note: '' });
  };

  const closePayModal = () => {
    setPayModal(null);
    setPayForm({ amount: '', note: '' });
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!payModal) return;

    const amount = Number(payForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter a valid payout amount');
      return;
    }
    if (amount > Number(payModal.balance)) {
      toast.error('Amount exceeds wallet balance');
      return;
    }

    setPayingId(payModal.mediatorId);
    try {
      await adminAPI.payMediatorReward(payModal.mediatorId, {
        amount,
        note: payForm.note?.trim() || undefined,
      });
      toast.success(`Paid ₹${formatMoney(amount)} to ${payModal.mediatorName}`);
      closePayModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payout failed');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div style={{ padding: '24px', flex: 1, backgroundColor: '#f8fafc', overflow: 'auto' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 800, color: '#1e293b', margin: '0 0 4px 0' }}>
            Mediator Rewards & Payouts
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
            View mediator wallet balances, process payouts, and review reward transaction history.
          </p>
        </div>

        <div style={{ ...cardStyle, marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Mediator Wallets</h2>
            <Button variant="secondary" onClick={fetchData} disabled={loading}>
              <Icon name="refresh-cw" size={16} /> Refresh
            </Button>
          </div>

          <div className="overflow-x-auto table-scroll-container">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  {['Mediator', 'Code', 'Orders this month', 'Earned', 'Paid Out', 'Balance', 'Action'].map((h) => (
                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center' }}>Loading wallets...</td></tr>
                ) : wallets.length === 0 ? (
                  <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No mediators found.</td></tr>
                ) : (
                  wallets.map((wallet) => (
                    <tr key={wallet.mediatorId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px', fontWeight: 600 }}>{wallet.mediatorName}</td>
                      <td style={{ padding: '16px', fontFamily: 'monospace' }}>{wallet.mediatorCode}</td>
                      <td style={{ padding: '16px' }}>{wallet.totalOrders}</td>
                      <td style={{ padding: '16px', color: '#059669' }}>₹{formatMoney(wallet.totalEarned)}</td>
                      <td style={{ padding: '16px' }}>₹{formatMoney(wallet.totalPaidOut)}</td>
                      <td style={{ padding: '16px', fontWeight: 700, color: '#0f766e' }}>₹{formatMoney(wallet.balance)}</td>
                      <td style={{ padding: '16px' }}>
                        <Button
                          variant="primary"
                          disabled={Number(wallet.balance) <= 0 || payingId === wallet.mediatorId}
                          onClick={() => openPayModal(wallet)}
                        >
                          Pay
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: 700 }}>Reward Transaction History</h2>
          <div className="overflow-x-auto table-scroll-container">
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  {['Date', 'Mediator', 'Type', 'Amount', 'Balance After', 'Description'].map((h) => (
                    <th key={h} style={{ padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {txLoading || loading ? (
                  <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center' }}>Loading transactions...</td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No transactions yet.</td></tr>
                ) : (
                  transactions.map((tx) => (
                    <tr key={tx.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{formatDateTime(tx.createdAt, '-')}</td>
                      <td style={{ padding: '16px', fontSize: '14px' }}>{tx.mediatorName || '-'}</td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: tx.type === 'CREDIT' ? '#d1fae5' : '#fee2e2',
                          color: tx.type === 'CREDIT' ? '#059669' : '#dc2626',
                        }}>
                          {tx.type === 'CREDIT' ? 'Credit' : 'Payout'}
                        </span>
                      </td>
                      <td style={{ padding: '16px', fontWeight: 700, color: tx.type === 'CREDIT' ? '#059669' : '#dc2626' }}>
                        {tx.type === 'CREDIT' ? '+' : '-'}₹{formatMoney(tx.amount)}
                      </td>
                      <td style={{ padding: '16px' }}>₹{formatMoney(tx.balanceAfter)}</td>
                      <td style={{ padding: '16px', fontSize: '14px', color: '#475569', maxWidth: '360px' }}>{tx.description}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {(txMeta.totalPages > 1 || txMeta.totalElements > 0) && (
            <PaginationBar
              page={txPage}
              totalPages={txMeta.totalPages}
              totalElements={txMeta.totalElements}
              pageSize={10}
              onPageChange={setTxPage}
              style={{ marginTop: '12px' }}
            />
          )}
        </div>
      </div>

      {payModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
        }}>
          <div style={{ ...cardStyle, width: '100%', maxWidth: '440px' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>Pay {payModal.mediatorName}</h3>
            <p style={{ margin: '0 0 16px 0', color: '#64748b', fontSize: '14px' }}>
              Available balance: ₹{formatMoney(payModal.balance)}
            </p>
            <form onSubmit={handlePay} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Amount (₹)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  max={payModal.balance}
                  value={payForm.amount}
                  onChange={(e) => setPayForm((prev) => ({ ...prev, amount: e.target.value }))}
                  style={inputStyle}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Note (optional)</label>
                <input
                  type="text"
                  value={payForm.note}
                  onChange={(e) => setPayForm((prev) => ({ ...prev, note: e.target.value }))}
                  placeholder="e.g. UPI transfer ref"
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button type="button" variant="secondary" onClick={closePayModal}>Cancel</Button>
                <Button type="submit" loading={payingId === payModal.mediatorId}>Confirm Payout</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPayouts;
